import type { TFunction } from 'i18next';

import { workerErrorText } from './errorText';

/** Never render server prose. Thin wrapper over the shared table in `errorText.ts` (1.0b). */
export function workerError(t: TFunction, error: unknown): string {
  return workerErrorText(t, error);
}

/** Where the day that blocks a new start is open (UX 1A-d). */
export interface OpenDayRef {
  jobId: string;
  timesheetId?: string;
  /** The server's code: same job (`WORK_DAY_STILL_OPEN`) or another one. */
  code: 'WORK_DAY_STILL_OPEN' | 'active_work_elsewhere';
}

const SAFE_ID = /^[A-Za-z0-9_-]+$/;

/**
 * A start refused because a day is still open — the server names that day
 * (`startWorkDayTx`: `details.jobId` / `details.timesheetId`). Anything else,
 * or a refusal without a usable job id, is null: the worker is then told the
 * reason but not sent anywhere we would have to guess.
 */
export function openDayOf(error: unknown): OpenDayRef | null {
  const details = (error as { details?: Record<string, unknown> | null } | null)?.details;
  if (!details || typeof details !== 'object') return null;
  const code =
    details.reason === 'WORK_DAY_STILL_OPEN'
      ? 'WORK_DAY_STILL_OPEN'
      : details.conflict === 'active_work_elsewhere'
        ? 'active_work_elsewhere'
        : null;
  const jobId =
    typeof details.jobId === 'string' && SAFE_ID.test(details.jobId) ? details.jobId : null;
  if (!code || !jobId) return null;
  return {
    code,
    jobId,
    ...(typeof details.timesheetId === 'string' && SAFE_ID.test(details.timesheetId)
      ? { timesheetId: details.timesheetId }
      : {}),
  };
}

/** Financial readiness has translated reasons; preserve existing diagnostics for other failures. */
export function billingError(t: TFunction, error: unknown): string {
  const reason = (error as { details?: { reason?: string; conflict?: string } })?.details?.reason;
  if (
    reason &&
    [
      'SHIFT_NOT_READY_FOR_BILLING',
      'HOURS_AWAITING_APPROVAL',
      'ORDER_UNPRICED',
      'MONTH_CLOSE_WORK_NOT_READY',
      'ORDER_NOT_COMPLETED',
      'INVOICE_WORK_LIMIT',
      'B2C_ROLE_ALLOCATION_REQUIRED',
      'B2C_PIECE_ORDER_NOT_READY',
    ].includes(reason)
  )
    return workerError(t, error);
  return error instanceof Error ? error.message : String(error);
}

/** The order's prepayment actions: the server's reason, in the reader's language. */
export function prepaymentError(t: TFunction, error: unknown): string {
  const reason = (error as { details?: { reason?: string } })?.details?.reason;
  if (reason)
    return t(`admin:jobs.prepayCard.errors.${reason}`, { defaultValue: billingError(t, error) });
  return billingError(t, error);
}

/** Staff actions need staff diagnostics, never instructions aimed at a worker. */
export function adminTimesheetError(t: TFunction, error: unknown): string {
  const e = error as { code?: string; details?: { reason?: string } };
  const code = e?.code?.replace(/^functions\//, '') ?? 'unknown';
  const fallback = t(`common:adminTimesheetErrors.${code}`, {
    defaultValue: t('common:adminTimesheetErrors.unknown'),
  });
  return e?.details?.reason
    ? t(`common:adminTimesheetErrors.${e.details.reason}`, { defaultValue: fallback })
    : fallback;
}
